import { loadStripe } from "@stripe/stripe-js";

/** Fields the server returns on a 402 that requires a bank challenge. */
export type StripePaymentActionChallenge = {
	paymentIntentId: string;
	clientSecret: string;
	publishableKey: string;
};

type ChallengeBody = {
	status?: string;
	client_secret?: string;
	publishable_key?: string;
	payment_intent_id?: string;
};

/**
 * Whether a failed charge response is asking the browser to run 3-D Secure
 * rather than reporting a decline.
 */
export const stripePaymentActionChallenge = (
	body: ChallengeBody | undefined,
): StripePaymentActionChallenge | null => {
	if (
		body?.status !== "requires_action" ||
		!body.client_secret ||
		!body.publishable_key ||
		!body.payment_intent_id
	) {
		return null;
	}

	return {
		paymentIntentId: body.payment_intent_id,
		clientSecret: body.client_secret,
		publishableKey: body.publishable_key,
	};
};

export type StripePaymentActionResult<T> =
	| { kind: "success"; data: T }
	| { kind: "failure"; error: string };

/**
 * Finish a payment Stripe would not take without a bank challenge, then let
 * the caller confirm it with the server.
 *
 * The browser runs handleNextAction; the server is the only thing that decides
 * the payment succeeded. confirm is domain-specific — top-up vs subscription —
 * so this module stays on the mechanical Stripe seam.
 */
export const completeStripePaymentAction = async <T>(args: {
	challenge: StripePaymentActionChallenge;
	confirm: (paymentIntentId: string) => Promise<{
		data?: T;
		error?: unknown;
	}>;
	confirmFailureMessage: string;
}): Promise<StripePaymentActionResult<T>> => {
	const stripe = await loadStripe(args.challenge.publishableKey);

	if (!stripe) {
		return {
			kind: "failure",
			error: "Couldn't reach Stripe to confirm your payment.",
		};
	}

	const { error: stripeError } = await stripe.handleNextAction({
		clientSecret: args.challenge.clientSecret,
	});

	if (stripeError) {
		return {
			kind: "failure",
			error: stripeError.message ?? "Your bank didn't confirm the payment.",
		};
	}

	const confirmed = await args.confirm(args.challenge.paymentIntentId);

	if (!confirmed.data) {
		return {
			kind: "failure",
			error:
				(confirmed.error as { error?: string } | undefined)?.error ??
				args.confirmFailureMessage,
		};
	}

	return { kind: "success", data: confirmed.data };
};
